import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useCart } from '@/context/MenuCartContext';
import { createOrder } from '@/api/orders';
import { previewPromotion } from '@/api/promotions';
import { hasDeliveryAddressFields } from '@/utils/deliveryRestaurants';
import { formatPhoneNumber, isPhoneComplete, PHONE_FORMAT_HINT } from '@/utils/phone';
import { useDeliverySuitableRestaurants } from '@/hooks/useDeliverySuitableRestaurants';
import { AddressModal } from '@/components/AddressModal';
import { formatDeliveryLine, formatStructuredAddressDisplay, isDeliveryAddressIncomplete, } from '@/types/address';
import { parseApiErrorMessage, parseOptionalFieldError, parseUnavailableProductsError, } from '@/api/errors';
import { readCommentDraft, writeCommentDraft, clearCommentDraft } from '@/pages/checkout/useCheckoutDraft';
import { useCheckoutMenus, useCheckoutProfile, useCheckoutRestaurants, } from '@/pages/checkout/useCheckoutData';
import { CheckoutOrderSummary } from '@/pages/checkout/CheckoutOrderSummary';
import { CheckoutDeliverySection } from '@/pages/checkout/CheckoutDeliverySection';
import { CheckoutBonusSection } from '@/pages/checkout/CheckoutBonusSection';
import { CheckoutPaymentSection } from '@/pages/checkout/CheckoutPaymentSection';
import '@/components/Cart.css';
import './Auth.css';
import './Checkout.css';
export function CheckoutPage() {
    const cart = useCart();
    const { cartItems, deliveryType, setDeliveryType, deliveryAddress, setDeliveryAddress, checkoutMenuId, setCheckoutMenuId, pickupRestaurantId, setPickupRestaurantId, setCartQuantity, setComboQuantity, removeProductFromCart, removeComboFromCart, clearCart, isProductAvailable, markUnavailableProductIds, clearUnavailableProductIds, } = cart;
    const { menus, menusLoading, menusError } = useCheckoutMenus(setCheckoutMenuId);
    const { restaurants, restaurantsLoading } = useCheckoutRestaurants(checkoutMenuId, setPickupRestaurantId);
    const { profile, bonusBalance, profileLoaded } = useCheckoutProfile();
    const [addressModalOpen, setAddressModalOpen] = useState(false);
    const [addressModalMode, setAddressModalMode] = useState('delivery');
    const { sortedSuitableRestaurants, suitableRestaurantsLoading, deliverySelectionWarning, deliveryAddressGeocoded, noSuitableRestaurants, showDeliveryRestaurantPicker, requiresDeliveryRestaurantPick, } = useDeliverySuitableRestaurants({
        deliveryType,
        checkoutMenuId,
        deliveryAddress,
        cartItems,
        restaurants,
        setPickupRestaurantId,
        suspended: addressModalOpen && addressModalMode === 'delivery',
    });
    const [comment, setComment] = useState(readCommentDraft);
    const [promoCode, setPromoCode] = useState('');
    const [promoCodeError, setPromoCodeError] = useState('');
    const [promoPreview, setPromoPreview] = useState(null);
    const [promoPreviewLoading, setPromoPreviewLoading] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [bonusMode, setBonusMode] = useState('SAVE_AND_EARN');
    const [bonusPointsToSpend, setBonusPointsToSpend] = useState(0);
    const [utensilsCount, setUtensilsCount] = useState(1);
    const [orderContactPhone, setOrderContactPhone] = useState('');
    const [requiresOrderConfirmation, setRequiresOrderConfirmation] = useState(false);
    useEffect(() => writeCommentDraft(comment), [comment]);
    const hasUnavailableItem = useMemo(() => cartItems.some((it) => it.kind === 'product' && !isProductAvailable(it.product)), [cartItems, isProductAvailable]);
    const total = cartItems.reduce((sum, it) => {
        if (it.kind === 'product' && !isProductAvailable(it.product))
            return sum;
        const price = it.kind === 'product'
            ? it.product.price + it.selectedModifiers.reduce((acc, m) => acc + m.price, 0)
            : it.combo.price;
        return sum + price * it.quantity;
    }, 0);
    const estimatedPayRub = useMemo(() => {
        const raw = promoPreview ? promoPreview.finalAmount : total;
        return Math.max(1, Math.floor(raw));
    }, [promoPreview, total]);
    const bonusPointsEarnedEstimate = useMemo(() => cartItems.reduce((sum, it) => {
        if (it.kind === 'product')
            return sum + (it.product.bonusAmount ?? 0) * it.quantity;
        return sum + it.combo.bonusAmount * it.quantity;
    }, 0), [cartItems]);
    const maxBonusSpend = useMemo(() => {
        if (bonusBalance == null)
            return 0;
        return Math.min(Math.floor(bonusBalance), Math.max(0, estimatedPayRub - 10));
    }, [bonusBalance, estimatedPayRub]);
    useEffect(() => {
        if (!profileLoaded || !profile)
            return;
        setOrderContactPhone((prev) => (prev.trim() ? prev : formatPhoneNumber(String(profile.phoneNumber ?? ''))));
    }, [profileLoaded, profile]);
    const needsOrderPhone = profileLoaded && (profile == null || !String(profile.phoneNumber ?? '').trim());
    const confirmationForcedByIncompleteAddress = useMemo(() => deliveryType === 'delivery' && deliveryAddress != null && isDeliveryAddressIncomplete(deliveryAddress), [deliveryType, deliveryAddress]);
    useEffect(() => {
        if (confirmationForcedByIncompleteAddress)
            setRequiresOrderConfirmation(true);
    }, [confirmationForcedByIncompleteAddress]);
    useEffect(() => {
        if (bonusMode === 'SPEND') {
            setBonusPointsToSpend((v) => (maxBonusSpend > 0 ? Math.min(Math.max(0, v), maxBonusSpend) : 0));
        }
    }, [bonusMode, maxBonusSpend]);
    const validateCheckoutInput = useCallback(() => {
        if (checkoutMenuId == null)
            return 'Выберите меню';
        if (restaurantsLoading || suitableRestaurantsLoading)
            return 'Подождите загрузки ресторанов';
        if (deliveryType === 'pickup') {
            const ok = pickupRestaurantId != null && restaurants.some((r) => r.id === pickupRestaurantId);
            if (!ok)
                return 'Выберите ресторан (кнопка «Адрес доставки»)';
        }
        if (deliveryType === 'delivery') {
            if (!hasDeliveryAddressFields(deliveryAddress))
                return 'Укажите адрес доставки (кнопка «Адрес доставки»)';
            if (noSuitableRestaurants)
                return 'Ни одна точка не может доставить все товары из корзины';
            if (restaurants.length > 1 && sortedSuitableRestaurants.length > 0 && pickupRestaurantId == null) {
                return 'Выберите адрес ресторана';
            }
            if (requiresDeliveryRestaurantPick && pickupRestaurantId == null)
                return 'Выберите адрес ресторана';
        }
        return null;
    }, [
        checkoutMenuId,
        deliveryAddress,
        deliveryType,
        noSuitableRestaurants,
        pickupRestaurantId,
        restaurants,
        restaurantsLoading,
        requiresDeliveryRestaurantPick,
        sortedSuitableRestaurants.length,
        suitableRestaurantsLoading,
    ]);
    const buildPreviewPayload = useCallback(() => ({
        menuId: checkoutMenuId,
        deliveryType: (deliveryType === 'delivery' ? 'COURIER' : 'PICKUP'),
        address: deliveryType === 'delivery' ? deliveryAddress : null,
        restaurantId: pickupRestaurantId,
        items: cartItems.map((it) => it.kind === 'product'
            ? { productId: it.product.id, quantity: it.quantity, modifierIds: it.selectedModifiers.map((m) => m.id) }
            : { comboId: it.combo.id, quantity: it.quantity }),
        comment: null,
        promoCode: promoCode.trim() || null,
    }), [cartItems, checkoutMenuId, deliveryAddress, deliveryType, pickupRestaurantId, promoCode]);
    const fetchCheckoutPreview = useCallback(async () => {
        if (checkoutMenuId == null || cartItems.length === 0 || menusLoading || restaurantsLoading || suitableRestaurantsLoading)
            return;
        if (validateCheckoutInput()) {
            setPromoPreview(null);
            return;
        }
        setPromoPreviewLoading(true);
        try {
            const res = await previewPromotion(buildPreviewPayload());
            setPromoPreview(res);
            clearUnavailableProductIds();
        }
        catch (err) {
            const unavailable = parseUnavailableProductsError(err);
            if (unavailable)
                markUnavailableProductIds(unavailable.productIds);
            setPromoPreview(null);
        }
        finally {
            setPromoPreviewLoading(false);
        }
    }, [
        buildPreviewPayload,
        cartItems.length,
        checkoutMenuId,
        clearUnavailableProductIds,
        markUnavailableProductIds,
        menusLoading,
        restaurantsLoading,
        suitableRestaurantsLoading,
        validateCheckoutInput,
    ]);
    useEffect(() => {
        const timer = window.setTimeout(() => void fetchCheckoutPreview(), 500);
        return () => window.clearTimeout(timer);
    }, [fetchCheckoutPreview]);
    const handleApplyPromo = useCallback(async () => {
        setPromoCodeError('');
        setSubmitError('');
        const trimmed = promoCode.trim();
        if (!trimmed) {
            setPromoCodeError('Введите промокод');
            return;
        }
        const validationError = validateCheckoutInput();
        if (validationError) {
            setPromoCodeError(validationError);
            return;
        }
        if (checkoutMenuId == null)
            return;
        setPromoPreviewLoading(true);
        try {
            const res = await previewPromotion({ ...buildPreviewPayload(), promoCode: trimmed });
            setPromoPreview(res);
        }
        catch (err) {
            setPromoCodeError(parseOptionalFieldError(err, 'promoCode') ?? parseApiErrorMessage(err, 'Не удалось применить промокод'));
            setPromoPreview(null);
        }
        finally {
            setPromoPreviewLoading(false);
        }
    }, [buildPreviewPayload, checkoutMenuId, promoCode, validateCheckoutInput]);
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');
        setPromoCodeError('');
        const validationError = validateCheckoutInput();
        if (validationError) {
            setSubmitError(validationError);
            return;
        }
        if (deliveryType === 'delivery' && promoPreview?.deliveryUnavailableReason && promoPreview.deliveryFeeRub == null) {
            setSubmitError(promoPreview.deliveryUnavailableReason);
            return;
        }
        if (hasUnavailableItem) {
            setSubmitError('Удалите недоступные товары из корзины перед оформлением');
            return;
        }
        if (deliveryType === 'delivery' && isDeliveryAddressIncomplete(deliveryAddress)) {
            const ok = window.confirm('Парадная, этаж или квартира не были указаны. Заказ будет доставлен к дому. Продолжить оплату?');
            if (!ok)
                return;
        }
        if (checkoutMenuId == null)
            return;
        if (bonusMode === 'SPEND' && maxBonusSpend <= 0) {
            setSubmitError('Списание бонусов недоступно (минимум 10 ₽ к оплате).');
            return;
        }
        if (!profileLoaded) {
            setSubmitError('Подождите загрузки профиля');
            return;
        }
        const phoneTrim = orderContactPhone.trim();
        if (deliveryType === 'delivery') {
            if (phoneTrim.length > 0 && !isPhoneComplete(phoneTrim)) {
                setSubmitError(`Телефон должен быть в формате ${PHONE_FORMAT_HINT}`);
                return;
            }
            if (needsOrderPhone && !isPhoneComplete(phoneTrim)) {
                setSubmitError(`Укажите номер телефона для связи по заказу (${PHONE_FORMAT_HINT})`);
                return;
            }
        }
        setSubmitting(true);
        try {
            const spendPoints = bonusMode === 'SPEND' && maxBonusSpend > 0
                ? Math.min(Math.max(0, bonusPointsToSpend), maxBonusSpend)
                : null;
            const res = await createOrder({
                menuId: checkoutMenuId,
                deliveryType: deliveryType === 'delivery' ? 'COURIER' : 'PICKUP',
                address: deliveryType === 'delivery' ? deliveryAddress : null,
                restaurantId: pickupRestaurantId,
                items: cartItems.map((it) => it.kind === 'product'
                    ? { productId: it.product.id, quantity: it.quantity, modifierIds: it.selectedModifiers.map((m) => m.id) }
                    : { comboId: it.combo.id, quantity: it.quantity }),
                comment: comment.trim() || null,
                promoCode: promoCode.trim() || null,
                bonusMode,
                bonusPointsToSpend: bonusMode === 'SPEND' ? spendPoints : undefined,
                utensilsCount: Math.min(99, Math.max(1, Math.floor(utensilsCount))),
                contactPhone: deliveryType === 'delivery' && isPhoneComplete(phoneTrim) ? phoneTrim : undefined,
                requiresOrderConfirmation: deliveryType === 'delivery' ? requiresOrderConfirmation : false,
            });
            const redirectUrl = res.redirectUrl?.trim();
            if (!redirectUrl)
                throw new Error(`Заказ #${res.orderId} создан, но ссылка на оплату не получена`);
            clearCart();
            clearCommentDraft();
            window.location.assign(redirectUrl);
        }
        catch (err) {
            const unavailable = parseUnavailableProductsError(err);
            if (unavailable) {
                markUnavailableProductIds(unavailable.productIds);
                setSubmitError(unavailable.message);
                return;
            }
            const promoErr = parseOptionalFieldError(err, 'promoCode');
            setPromoCodeError(promoErr ?? '');
            setSubmitError(promoErr ? '' : parseApiErrorMessage(err, 'Не удалось оформить заказ'));
        }
        finally {
            setSubmitting(false);
        }
    };
    const pickupLabel = pickupRestaurantId == null
        ? 'Выберите точку самовывоза'
        : formatStructuredAddressDisplay(restaurants.find((r) => r.id === pickupRestaurantId)?.address) || 'Точка выбрана';
    const deliveryRestaurantLabel = useMemo(() => {
        if (deliveryType !== 'delivery')
            return null;
        const list = restaurants.length > 1 && sortedSuitableRestaurants.length > 0 ? sortedSuitableRestaurants : restaurants;
        const r = list.find((x) => x.id === pickupRestaurantId);
        return r ? formatStructuredAddressDisplay(r.address) : null;
    }, [deliveryType, pickupRestaurantId, restaurants, sortedSuitableRestaurants]);
    const deliveryFeeLabel = useMemo(() => {
        if (deliveryType !== 'delivery')
            return null;
        if (promoPreviewLoading)
            return 'рассчитывается…';
        if (promoPreview?.deliveryFeeRub != null)
            return `${promoPreview.deliveryFeeRub.toFixed(2)} ₽`;
        if (promoPreview?.deliveryUnavailableReason)
            return 'недоступна';
        return 'будет рассчитана после выбора адреса';
    }, [deliveryType, promoPreview, promoPreviewLoading]);
    if (cartItems.length === 0)
        return _jsx(Navigate, { to: "/menu", replace: true });
    return (_jsxs("div", { className: "checkout page-container", children: [_jsx("h1", { className: "checkout__page-title", children: "\u041E\u0444\u043E\u0440\u043C\u043B\u0435\u043D\u0438\u0435 \u0437\u0430\u043A\u0430\u0437\u0430" }), _jsxs("form", { className: "checkout__layout", onSubmit: handleSubmit, children: [_jsx("div", { className: "checkout__column", children: _jsx(CheckoutOrderSummary, { cartItems: cartItems, bonusMode: bonusMode, isProductAvailable: isProductAvailable, promoPreview: promoPreview, total: total, deliveryType: deliveryType, deliveryFeeLabel: deliveryFeeLabel, promoPreviewLoading: promoPreviewLoading, onIncrementProduct: (lineId) => {
                                const item = cartItems.find((it) => it.kind === 'product' && it.lineId === lineId);
                                if (item?.kind === 'product')
                                    setCartQuantity(lineId, item.quantity + 1);
                            }, onDecrementProduct: (lineId) => {
                                const item = cartItems.find((it) => it.kind === 'product' && it.lineId === lineId);
                                if (item?.kind === 'product')
                                    setCartQuantity(lineId, item.quantity - 1);
                            }, onIncrementCombo: (id) => {
                                const item = cartItems.find((it) => it.kind === 'combo' && it.combo.id === id);
                                if (item?.kind === 'combo')
                                    setComboQuantity(id, item.quantity + 1);
                            }, onDecrementCombo: (id) => {
                                const item = cartItems.find((it) => it.kind === 'combo' && it.combo.id === id);
                                if (item?.kind === 'combo')
                                    setComboQuantity(id, item.quantity - 1);
                            }, onRemoveProduct: removeProductFromCart, onRemoveCombo: removeComboFromCart }) }), _jsxs("div", { className: "checkout__column", children: [_jsx(CheckoutDeliverySection, { deliveryType: deliveryType, setDeliveryType: setDeliveryType, menus: menus, menusLoading: menusLoading, menusError: menusError, checkoutMenuId: checkoutMenuId, setCheckoutMenuId: (id) => setCheckoutMenuId(id), setPickupRestaurantId: (id) => setPickupRestaurantId(id), deliveryLabel: deliveryAddress ? formatDeliveryLine(deliveryAddress) : 'Укажите адрес доставки', pickupLabel: pickupLabel, showDeliveryRestaurantPicker: showDeliveryRestaurantPicker, deliveryRestaurantLabel: deliveryRestaurantLabel, onOpenAddressModal: (mode) => {
                                    setAddressModalMode(mode);
                                    setAddressModalOpen(true);
                                }, promoCode: promoCode, setPromoCode: setPromoCode, promoCodeError: promoCodeError, setPromoCodeError: setPromoCodeError, setPromoPreview: setPromoPreview, promoPreviewLoading: promoPreviewLoading, onApplyPromo: () => void handleApplyPromo(), comment: comment, setComment: setComment, utensilsCount: utensilsCount, setUtensilsCount: setUtensilsCount, profileLoaded: profileLoaded, orderContactPhone: orderContactPhone, setOrderContactPhone: setOrderContactPhone, needsOrderPhone: needsOrderPhone, requiresOrderConfirmation: requiresOrderConfirmation, setRequiresOrderConfirmation: setRequiresOrderConfirmation, confirmationForcedByIncompleteAddress: confirmationForcedByIncompleteAddress }), _jsx(CheckoutBonusSection, { bonusBalance: bonusBalance, bonusMode: bonusMode, setBonusMode: setBonusMode, bonusPointsEarnedEstimate: bonusPointsEarnedEstimate, maxBonusSpend: maxBonusSpend, bonusPointsToSpend: bonusPointsToSpend, setBonusPointsToSpend: setBonusPointsToSpend }), _jsx(CheckoutPaymentSection, { submitError: submitError, submitting: submitting, disabled: submitting ||
                                    menusLoading ||
                                    !profileLoaded ||
                                    hasUnavailableItem ||
                                    noSuitableRestaurants ||
                                    (bonusBalance != null && bonusMode === 'SPEND' && maxBonusSpend <= 0), disabledTitle: hasUnavailableItem ? 'Удалите недоступные товары перед оформлением' : undefined })] })] }), _jsx(AddressModal, { open: addressModalOpen, mode: addressModalMode, onClose: () => setAddressModalOpen(false), selectedMenuName: menus.find((m) => m.id === checkoutMenuId)?.name ?? null, restaurants: restaurants, selectedRestaurantId: pickupRestaurantId, onConfirmPickup: (id) => setPickupRestaurantId(id), deliveryAddress: deliveryAddress, onConfirmDelivery: (t) => setDeliveryAddress(t), suitableRestaurants: sortedSuitableRestaurants, suitableRestaurantsLoading: suitableRestaurantsLoading, deliverySelectionWarning: deliverySelectionWarning, deliveryAddressGeocoded: deliveryAddressGeocoded, noSuitableRestaurants: noSuitableRestaurants })] }));
}
